import Vue from "vue";
import App from "./App.vue";
import { initRouter } from "./router";
import store from "./store";
import { i18n, supportedLanguages } from "./i18n/i18n";
import { loadProductionLocale } from "@/i18n/loadProductionLocale";
import { setLocale } from "@/i18n/setLocale";
import PrimeVue from "primevue/config";
import { BootstrapVue, IconsPlugin } from "bootstrap-vue";
import { Splitpanes, Pane } from "splitpanes";
import initHotjar from "./init/hotjar";
import initSentry from "./init/sentry";
import initModalManager from "./init/modalManager";
import initializeGrowthBook from "./init/growthbook";
import initShowToast from "./init/toast";
import initTrackEvent from "./init/trackEvent";
import initOpenForm from "./init/openForm";
import initClerk from "./init/clerk";
import GlobalComponents from "./init/globalComponents";
import { initEventBus } from "./init/eventBus";

import "./assets/main.scss";
import "splitpanes/dist/splitpanes.css";

const isTest = process.env.NODE_ENV === "test";

// NOTE: PRIMEVUE for Vue2 documentation: https://bidx.github.io/prime-vue-v2-docs/#/
Vue.use(PrimeVue);
Vue.use(BootstrapVue);
Vue.use(IconsPlugin);
// eslint-disable-next-line vue/multi-word-component-names
Vue.component("Splitpanes", Splitpanes);
// eslint-disable-next-line vue/multi-word-component-names
Vue.component("Pane", Pane);
Vue.use(GlobalComponents);
initModalManager(Vue);
initShowToast(Vue);
initOpenForm(Vue);
initEventBus(Vue);

if (!isTest) {
  initHotjar(Vue);
  initSentry(Vue);
  initTrackEvent(Vue);
}

Vue.config.productionTip = false;

// Expose a global helper so QA / devs can switch locale from the browser console:
//   __eg.setLocale('uk')
//   __eg.locale
//   __eg.locales
window.__eg = {
  setLocale,
  get locale() {
    return i18n.locale;
  },
  locales: supportedLanguages,
};

// After a new deployment, cached HTML may reference chunk files that no longer
// exist on the server. Reload once per version to pick up the new bundle.
// Storing the reloaded version in sessionStorage prevents infinite loops when
// the server itself is broken (same version → stop retrying).
const _CHUNK_RELOAD_KEY = "__eg_chunk_reload_v";
window.addEventListener("unhandledrejection", (event) => {
  const message = event.reason?.message || "";
  if (!/Loading (CSS )?chunk \S+ failed/.test(message)) return;
  const currentVersion = process.env.VUE_APP_VERSION;
  if (!currentVersion) return;
  if (sessionStorage.getItem(_CHUNK_RELOAD_KEY) === currentVersion) return;
  sessionStorage.setItem(_CHUNK_RELOAD_KEY, currentVersion);
  window.location.reload();
});

export let app = {};

const loadLocales = async () => {
  if (process.env.NODE_ENV === "production") {
    await loadProductionLocale(i18n.locale);
  }
};

if (!isTest) {
  Promise.all([initializeGrowthBook(), initClerk(Vue), loadLocales()]).then(
    ([gbInstance]) => {
      const router = initRouter();
      app = new Vue({
        router,
        store,
        i18n,
        data() {
          return {
            growthBook: gbInstance,
            clerk: null,
          };
        },
        async beforeCreate() {
          // Provide the GrowthBook instance
          this.$options.provide = {
            growthBook: gbInstance,
          };
          this.clerk = await this.$initClerk();
        },
        render: (h) => h(App),
      }).$mount("#app");
    }
  );
}
